import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pin, X, Plus, Archive, ArchiveRestore, Trash2, Lock, Unlock, MoreVertical, Copy, Tag, History, Share2, ImagePlus, FilePlus2, Mic, Camera, Search } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { ColorPicker, ReminderPicker } from "@/components/QuickCreate";
import ImageAttacher from "@/components/ImageAttacher";
import DocumentAttacher from "@/components/DocumentAttacher";
import ShareDialog from "@/components/ShareDialog";
import AudioRecorder from "@/components/AudioRecorder";
import FindInNote from "@/components/FindInNote";
import { newGuestLock, verifyGuestLock } from "@/lib/guestLock";
import RichTextEditor from "@/components/RichTextEditor";
import VersionsDialog from "@/components/VersionsDialog";
import { colorClass } from "@/lib/noteColors";
import { cn } from "@/lib/utils";
import { useT } from "@/i18n/LanguageContext";
import { api, uploadImage } from "@/lib/api";
import { duplicateNote, listNoteVersions, updateNote, emitChange, createLabel } from "@/lib/offlineStore";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { merge3 } from "@/lib/merge3";

const MERGE_FIELDS = ["title", "content", "is_checklist", "checklist_items", "color", "images", "attachments"];

const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);

// Convert rich-text HTML into visually distinct lines so "To checklist" never
// produces a single row that reads like "<ul><li>...</li></ul>".
function htmlToLines(html) {
  if (!html) return [];
  if (typeof DOMParser === "undefined") return [];
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return [];
  root.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  root.querySelectorAll("p, li, div").forEach((el) => {
    el.append("\n");
  });
  return (root.textContent || "")
    .split("\n")
    .map((s) => s.replace(/^[•\u2022\-\*]\s*/, "").trim())
    .filter(Boolean);
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Shallow equality for the fields a user can edit in-place. Used by the
// live-collab poller to detect whether the local draft has unsaved changes
// before deciding whether to adopt a collaborator's remote update silently.
function isSameNote(a, b) {
  if (!a || !b) return false;
  if ((a.title || "") !== (b.title || "")) return false;
  if ((a.content || "") !== (b.content || "")) return false;
  if (!!a.is_checklist !== !!b.is_checklist) return false;
  const ai = JSON.stringify((a.checklist_items || []).map((i) => ({ text: i.text, checked: !!i.checked })));
  const bi = JSON.stringify((b.checklist_items || []).map((i) => ({ text: i.text, checked: !!i.checked })));
  if (ai !== bi) return false;
  if ((a.color || "default") !== (b.color || "default")) return false;
  if (JSON.stringify(a.labels || []) !== JSON.stringify(b.labels || [])) return false;
  if (!!a.pinned !== !!b.pinned) return false;
  if ((a.reminder || null) !== (b.reminder || null)) return false;
  if (JSON.stringify(a.images || []) !== JSON.stringify(b.images || [])) return false;
  return true;
}

function ToolbarBtn({ children, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      data-testid={testid}
      className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-stone-200/70 dark:hover:bg-stone-700/70 text-stone-700 dark:text-stone-200"
    >
      {children}
    </button>
  );
}

export default function NoteEditor({ note, open, onClose, onSave, onTrash, onArchive, labels = [], onDuplicated, onManageLabels, onDeleteEmpty }) {
  const { t } = useT();
  const { authState } = useAuth();
  const [draft, setDraft] = useState(note);
  const [unlocked, setUnlocked] = useState(false);
  // Refs the compact Image/Document attachers write their <input> onto, so
  // the footer icon buttons can trigger the native file picker.
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  // Hidden <input type="file" capture="environment"> for the Camera button.
  // On phones this opens the camera directly; on desktop it opens the file
  // picker so the feature still works everywhere.
  const cameraInputRef = useRef(null);
  // Container that wraps everything the "Find in note" bar should search —
  // title + content + checklist items + attachments. The find bar highlights
  // DOM text nodes inside this ref.
  const findRootRef = useRef(null);
  const [passphrase, setPassphrase] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [audioOpen, setAudioOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [cameraUploading, setCameraUploading] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const textareaRef = useRef(null);
  // Snapshot of the note as last loaded from server. Live-collab polling
  // compares against this (not against `draft`) so background pulls don't
  // mis-fire "dirty" and we can safely merge collaborator saves when the
  // local user hasn't touched anything.
  const baselineRef = useRef(note);
  const draftRef = useRef(note);
  draftRef.current = draft;

  useEffect(() => {
    setDraft(note);
    baselineRef.current = note;
    setUnlocked(false);
    setPassphrase("");
    setUnlockError("");
    // Intentionally depend on note?.id, NOT `note` — the parent recreates
    // the note object on every render; only an id change means we're
    // switching to a different note and should reset local draft state.
  }, [note?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // CRITICAL — every time the editor dialog re-opens (even on the SAME
  // note), reset `unlocked` to false. Without this, a user who typed the
  // passphrase, read the note, closed the dialog, then re-opened the SAME
  // card would see the content without being prompted again — effectively
  // bypassing the lock. The useEffect above only fires on note.id change,
  // so re-opening a locked note kept the old `unlocked=true` session.
  useEffect(() => {
    if (open && note?.locked) {
      setUnlocked(false);
      setPassphrase("");
      setUnlockError("");
    }
  }, [open, note?.id, note?.locked]);

  // Ctrl/Cmd+F — open the Find bar. Only when the editor dialog is open
  // and the note is not behind the lock gate.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const isFind = (e.key === "f" || e.key === "F") && (e.ctrlKey || e.metaKey);
      if (isFind) {
        e.preventDefault();
        setFindOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Live collaborator sync: while the editor is open, pull the latest note
  // every 5s. If the server copy is newer and the local user hasn't edited
  // (draft matches baseline), silently adopt the collaborator's changes. If
  // the user has edits in-flight, surface a toast so they can decide.
  useEffect(() => {
    if (!open || !draft?.id || draft.locked) return;
    // Guest users have no backend copy — skip the poll so we don't 401 every 5s.
    if (authState !== "user") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/notes/${draft.id}`);
        if (cancelled || !data) return;
        const base = baselineRef.current;
        if (!base) return;
        if (data.updated_at && base.updated_at && data.updated_at === base.updated_at) return;
        const current = draftRef.current;
        const dirty = !isSameNote(current, base);
        baselineRef.current = data;
        if (!dirty) {
          setDraft(data);
        } else {
          // Owner is mid-edit: fold the collaborator's changes into the draft
          // without throwing away anything the owner typed.
          const pickF = (n) => Object.fromEntries(MERGE_FIELDS.map((f) => [f, n?.[f]]));
          const mine = merge3(data, pickF(current), pickF(base));
          setDraft({ ...current, ...pickF(data), ...mine, updated_at: data.updated_at });
        }
        toast.message(t("collab.updatedToast"), { duration: 2500 });
      } catch (err) {
        // Transient network blips while polling — don't spam the user with
        // a toast every 5s. Log once per failure so QA/ops can see it.
        if (err?.response?.status && err.response.status !== 404) {
          console.warn("[collab-poll] fetch failed:", err.response.status, err.message);
        }
      }
    };
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, draft?.id, draft?.locked, authState]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) return null;

  const isLockedGate = draft.locked && !unlocked;

  const doUnlock = async () => {
    if (!passphrase.trim()) return;
    const isGuest = authState !== "user";
    if (isGuest) {
      // Guest unlock: verify against the salt+hash stored in localStorage;
      // the note content is already in localStorage so no server call is needed.
      try {
        const notes = JSON.parse(localStorage.getItem("keepflow.notes") || "[]");
        const stored = notes.find((n) => n.id === draft.id);
        const ok = await verifyGuestLock(passphrase.trim(), stored?.guest_lock_salt, stored?.guest_lock_hash);
        if (!ok) { setUnlockError(t("unlock.wrong")); return; }
        setDraft({ ...stored, locked: true });   // keep locked flag; just unlock the view
        baselineRef.current = { ...stored, locked: true };
        setUnlocked(true);
        setUnlockError("");
      } catch (e) {
        setUnlockError("Could not unlock");
      }
      return;
    }
    try {
      const { data } = await api.post(`/notes/${draft.id}/unlock`, { passphrase: passphrase.trim() });
      // Replace draft with full content (note stays locked server-side)
      setDraft({ ...data, locked: true });
      baselineRef.current = { ...data, locked: true };
      setUnlocked(true);
      setUnlockError("");
    } catch (e) {
      if (e.response?.status === 401) setUnlockError(t("unlock.wrong"));
      else setUnlockError("Could not unlock");
    }
  };

  const toggleLock = async () => {
    const isGuest = authState !== "user";
    if (draft.locked) {
      // Prompt for current passphrase and remove the lock entirely
      const pass = window.prompt(t("notePw.enterToUnlock"));
      if (!pass) return;
      if (isGuest) {
        // Guest unlock: compare against the salt+hash stored in localStorage.
        try {
          const notes = JSON.parse(localStorage.getItem("keepflow.notes") || "[]");
          const stored = notes.find((n) => n.id === draft.id);
          const ok = await verifyGuestLock(pass, stored?.guest_lock_salt, stored?.guest_lock_hash);
          if (!ok) { toast.error(t("notePw.wrong")); return; }
          const next = notes.map((n) =>
            n.id === draft.id ? { ...n, locked: false, guest_lock_salt: undefined, guest_lock_hash: undefined } : n
          );
          localStorage.setItem("keepflow.notes", JSON.stringify(next));
          setDraft({ ...draft, locked: false });
          setUnlocked(false);
          toast.success(t("notePw.unlocked"));
        } catch (err) {
          console.warn("[guest-lock] unlock failed:", err?.message || err);
          toast.error("Failed to unlock");
        }
        return;
      }
      try {
        const { data } = await api.post(`/notes/${draft.id}/remove-lock`, { passphrase: pass });
        setDraft({ ...draft, ...data, locked: false });
        setUnlocked(false);
        try {
          const notes = JSON.parse(localStorage.getItem("keepflow.notes") || "[]");
          const next = notes.map((n) => (n.id === draft.id ? { ...n, locked: false } : n));
          localStorage.setItem("keepflow.notes", JSON.stringify(next));
        } catch (err) {
          console.warn("[note-lock] local mirror failed:", err?.message || err);
        }
        toast.success(t("notePw.unlocked"));
      } catch (err) {
        console.warn("[note-lock] remove-lock failed:", err?.response?.status, err?.message);
        toast.error(t("notePw.wrong"));
      }
    } else {
      const pass = window.prompt(t("notePw.setPassphrase"));
      if (!pass || pass.length < 3) return;
      // Save any pending title/content/image/attachment edits BEFORE locking,
      // otherwise the server (or local store) still holds the pre-edit copy
      // and newly-attached pictures vanish after the lock lands. Previously
      // the user had to close + reopen the note to get images persisted
      // before locking — now one click is enough.
      try {
        await onSave(draft.id, {
          title: draft.title,
          content: draft.content,
          is_checklist: draft.is_checklist,
          checklist_items: (draft.checklist_items || []).filter((i) => i.text.trim()),
          color: draft.color,
          labels: draft.labels || [],
          pinned: draft.pinned,
          reminder: draft.reminder || null,
          reminder_recurrence: draft.reminder_recurrence || null,
          images: draft.images || [],
          attachments: draft.attachments || [],
        });
      } catch (err) {
        console.warn("[note-lock] pre-lock save failed:", err?.message || err);
      }
      if (isGuest) {
        try {
          const { salt, hash } = await newGuestLock(pass);
          const notes = JSON.parse(localStorage.getItem("keepflow.notes") || "[]");
          const next = notes.map((n) =>
            n.id === draft.id
              ? { ...n, locked: true, guest_lock_salt: salt, guest_lock_hash: hash }
              : n
          );
          localStorage.setItem("keepflow.notes", JSON.stringify(next));
          setDraft({ ...draft, locked: true });
          // Notify every subscribed component (KeepApp notes list) so the
          // card refreshes to the "Password-protected" state immediately.
          // Without this, the local cache was updated but the in-memory
          // React state kept `locked: false`, and reopening the card showed
          // the content without asking for a passphrase.
          emitChange();
          toast.success(t("notePw.locked"));
        } catch (err) {
          console.warn("[guest-lock] lock failed:", err?.message || err);
          toast.error("Failed to lock");
        }
        return;
      }
      try {
        const { data: lockedNote } = await api.post(`/notes/${draft.id}/lock`, { passphrase: pass });
        setDraft({ ...draft, locked: true, updated_at: lockedNote?.updated_at || draft.updated_at });
        baselineRef.current = { ...draft, locked: true, updated_at: lockedNote?.updated_at || draft.updated_at };
        try {
          const notes = JSON.parse(localStorage.getItem("keepflow.notes") || "[]");
          const next = notes.map((n) =>
            n.id === draft.id ? { ...n, locked: true, content: "", checklist_items: [], updated_at: lockedNote?.updated_at || n.updated_at } : n
          );
          localStorage.setItem("keepflow.notes", JSON.stringify(next));
        } catch (err) {
          console.warn("[note-lock] local mirror failed:", err?.message || err);
        }
        // Notify subscribers — same reason as the guest branch above.
        emitChange();
        toast.success(t("notePw.locked"));
      } catch (err) {
        console.warn("[note-lock] lock failed:", err?.response?.status, err?.message);
        toast.error("Failed to lock");
      }
    }
  };

  const duplicate = async () => {
    try {
      const copy = await duplicateNote(draft.id);
      toast.success("Copy created");
      onDuplicated?.(copy);
      // Let the DropdownMenu finish its close animation BEFORE we tear down
      // the parent Dialog — closing both at once leaves Radix's scroll-lock
      // in a stuck state (`pointer-events:none` lingers on <body>), which
      // caused every note card to be unclickable until the user refreshed.
      setTimeout(() => onClose(), 120);
    } catch (e) {
      if ((e?.message || "").toLowerCase().includes("unlock")) toast.error("Unlock the note first");
      else toast.error("Could not duplicate");
    }
  };

  const toggleLabel = (name) => {
    const has = (draft.labels || []).includes(name);
    const next = has ? draft.labels.filter((n) => n !== name) : [...(draft.labels || []), name];
    setDraft({ ...draft, labels: next });
  };

  // Inline "Create new label" from inside the editor: prompts for a name,
  // creates it in the local store, and ATTACHES it to the current note so
  // the user doesn't have to open the labels manager, create a label, and
  // then re-open the note to tag it. Fire-and-forget — the sidebar refreshes
  // its labels list via the offlineStore subscribe listener.
  //
  // IMPORTANT: Radix DropdownMenuItem's onSelect runs inside the pointer-up
  // event; calling window.prompt synchronously blocks that event, which
  // cancels the whole chain and leaves nothing happening. We therefore
  // schedule the prompt on the next tick with setTimeout(0) so the menu
  // can finish dismissing first.
  const createLabelAndAttach = () => {
    setTimeout(async () => {
      const name = window.prompt("New label name");
      if (!name) return;
      const trimmed = name.trim();
      if (!trimmed) return;
      try {
        const label = await createLabel(trimmed);
        const labelName = label?.name || trimmed;
        setDraft((d) => {
          const existing = d.labels || [];
          if (existing.includes(labelName)) return d;
          return { ...d, labels: [...existing, labelName] };
        });
        toast.success(`Label "${labelName}" added`);
      } catch (err) {
        toast.error("Could not create label");
        console.warn("[editor] create-label failed:", err);
      }
    }, 0);
  };

  // Wrap selected text in textarea with the given markers (or insert at caret).
  const wrapSelection = () => {};
  const insertBulletList = () => {};

  const commit = async () => {
    if (isLockedGate) { onClose(); return; }
    const payload = {
      title: draft.title,
      content: draft.content,
      is_checklist: draft.is_checklist,
      checklist_items: (draft.checklist_items || []).filter((i) => i.text.trim()),
      color: draft.color,
      labels: draft.labels || [],
      pinned: draft.pinned,
      reminder: draft.reminder || null,
      reminder_recurrence: draft.reminder_recurrence || null,
      images: draft.images || [],
      attachments: draft.attachments || [],
    };
    const textOf = (html) => (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;|\u00a0/g, " ").trim();
    const isEmpty = !(payload.title || "").trim() && !textOf(payload.content)
      && !payload.checklist_items.length && !payload.images.length && !payload.attachments.length;
    if (isEmpty && onDeleteEmpty) { await onDeleteEmpty(draft.id); onClose(); return; }
    await onSave(draft.id, { ...payload, _base: baselineRef.current });
    onClose();
  };

  const updateItem = (id, patch) => {
    setDraft((n) => ({
      ...n,
      checklist_items: n.checklist_items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    }));
  };
  const addItem = () => {
    setDraft((n) => ({
      ...n,
      is_checklist: true,
      checklist_items: [...(n.checklist_items || []), { id: genId(), text: "", checked: false }],
    }));
  };
  const removeItem = (id) => {
    setDraft((n) => ({ ...n, checklist_items: n.checklist_items.filter((i) => i.id !== id) }));
  };

  // Camera: open the hidden <input capture=environment> which lets phone
  // browsers launch the camera app directly. Desktop falls back to the
  // normal file picker (also acceptable).
  const onCameraChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setCameraUploading(true);
    try {
      const paths = [];
      for (const file of files) {
        if (!file.type?.startsWith("image/")) continue;
        try { const { path } = await uploadImage(file); paths.push(path); }
        catch (err) { console.warn("[camera] upload failed:", err?.message || err); toast.error("Photo upload failed"); }
      }
      if (paths.length) {
        setDraft((d) => ({ ...d, images: [...(d.images || []), ...paths] }));
        toast.success(`Added ${paths.length} photo${paths.length > 1 ? "s" : ""}`);
      }
    } finally {
      setCameraUploading(false);
    }
  };

  // Audio: AudioRecorder uploads the clip and hands us an attachment row
  // which we drop into the draft's attachments array (same shape as docs).
  const onAudioSaved = (att) => {
    setDraft((d) => ({ ...d, attachments: [...(d.attachments || []), att] }));
    toast.success("Voice note added");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) commit(); }}>
      <DialogContent
        className={cn("max-w-2xl p-0 border-2 overflow-hidden flex flex-col max-h-[90vh]", colorClass(draft.color))}
        data-testid="note-editor"
        onEscapeKeyDown={(e) => {
          // When the Find bar is open, Escape should close the Find bar
          // only — NOT the whole editor. Radix fires this before closing;
          // calling preventDefault() cancels the close.
          if (findOpen) {
            e.preventDefault();
            setFindOpen(false);
          }
        }}
      >
        <DialogTitle className="sr-only">Edit note</DialogTitle>
        <DialogDescription className="sr-only">Edit the selected note.</DialogDescription>

        {isLockedGate ? (
          <div className="p-10 flex flex-col items-center text-center space-y-4" data-testid="editor-lock-gate">
            <div className="h-14 w-14 rounded-2xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
              <Lock className="h-7 w-7 text-amber-700 dark:text-amber-300" />
            </div>
            <h2 className="font-display font-bold text-xl">{draft.title || t("notePw.lockedCard")}</h2>
            <p className="text-sm text-stone-500 dark:text-stone-400">{t("notePw.enterToUnlock")}</p>
            <div className="w-full max-w-xs space-y-2">
              <Input
                type="password"
                value={passphrase}
                onChange={(e) => { setPassphrase(e.target.value); setUnlockError(""); }}
                onKeyDown={(e) => e.key === "Enter" && doUnlock()}
                placeholder={t("unlock.placeholder")}
                autoFocus
                data-testid="editor-unlock-input"
              />
              {unlockError && <p className="text-xs text-rose-600" data-testid="editor-unlock-error">{unlockError}</p>}
              <Button onClick={doUnlock} disabled={!passphrase.trim()} className="w-full rounded-full" data-testid="editor-unlock-btn">
                <Unlock className="h-4 w-4 mr-2" />{t("unlock.unlock")}
              </Button>
            </div>
          </div>
        ) : (
        <div className="p-5 space-y-3 relative overflow-y-auto flex-1 min-h-0" ref={findRootRef}>
          <FindInNote
            containerRef={findRootRef}
            open={findOpen}
            onClose={() => setFindOpen(false)}
          />
          {/* Image + document dropzones back at the top (per user request)
              so people see "I can drop files here" the moment the editor
              opens. The inline icon buttons in the footer still work. */}
          <ImageAttacher
            images={draft.images || []}
            onChange={(imgs) => setDraft({ ...draft, images: imgs })}
            triggerRef={imageInputRef}
          />
          <DocumentAttacher
            attachments={draft.attachments || []}
            onChange={(docs) => setDraft({ ...draft, attachments: docs })}
            triggerRef={docInputRef}
          />
          <div className="flex items-start gap-2">
            <Input
              placeholder={t("editor.title")}
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="border-0 shadow-none focus-visible:ring-0 px-0 text-lg font-semibold bg-transparent"
              data-testid="editor-title"
            />
            <button
              onClick={() => setDraft({ ...draft, pinned: !draft.pinned })}
              className={cn(
                "h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60",
                draft.pinned && "text-amber-700 dark:text-amber-300"
              )}
              data-testid="editor-pin"
              aria-label="Pin"
            >
              <Pin className={cn("h-4 w-4", draft.pinned && "fill-current")} />
            </button>
            <button
              onClick={toggleLock}
              className={cn(
                "h-9 shrink-0 px-3 inline-flex items-center gap-1.5 rounded-full border text-xs font-medium transition-colors",
                draft.locked
                  ? "border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40"
                  : "border-border hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
              )}
              data-testid="editor-header-lock"
              aria-label={draft.locked ? "Remove lock" : "Lock note"}
              title={draft.locked ? "Remove the password from this note" : "Lock this note with a password"}
            >
              {draft.locked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{draft.locked ? "Unlock" : "Lock"}</span>
            </button>
          </div>

          {draft.is_checklist ? (
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
              {(draft.checklist_items || []).map((it, idx) => (
                <div key={it.id} className="flex items-center gap-2">
                  <button
                    onClick={() => updateItem(it.id, { checked: !it.checked })}
                    className={cn(
                      "h-4 w-4 rounded border border-stone-400 dark:border-stone-500 flex items-center justify-center",
                      it.checked && "bg-stone-900 border-stone-900 dark:bg-stone-100 dark:border-stone-100"
                    )}
                    aria-label="Toggle"
                  >
                    {it.checked && <span className="text-[10px] text-white dark:text-stone-900">✓</span>}
                  </button>
                  <input
                    value={it.text}
                    onChange={(e) => updateItem(it.id, { text: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); addItem(); }
                    }}
                    autoFocus={idx === (draft.checklist_items || []).length - 1 && !it.text}
                    className={cn(
                      "flex-1 bg-transparent outline-none text-sm py-1",
                      it.checked && "line-through text-stone-400"
                    )}
                  />
                  <button
                    onClick={() => removeItem(it.id)}
                    className="h-6 w-6 inline-flex items-center justify-center rounded-full text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <RichTextEditor
              value={draft.content}
              onChange={(html) => setDraft({ ...draft, content: html })}
              placeholder={t("editor.placeholder")}
              testid="editor"
              minHeight="200px"
            />
          )}

          {/* Dropzones were moved to the top of the editor — this used
              to render compact (icon-only) duplicates here. */}

          <div className="flex items-center gap-2 pt-2 border-t border-border mt-2">
            {/* Horizontal scroll on narrow screens so NO icon ever hides.
                The icons themselves are also a touch smaller on <sm so 10+
                controls fit without needing a second row. */}
            <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto no-scrollbar flex-1 min-w-0 py-0.5">
              <ColorPicker current={draft.color} onPick={(k) => setDraft({ ...draft, color: k })} />
              <ReminderPicker
                value={draft.reminder}
                onChange={(d) => setDraft({ ...draft, reminder: d })}
                recurrence={draft.reminder_recurrence || "none"}
                onRecurrenceChange={(r) => setDraft({ ...draft, reminder_recurrence: r })}
              />
              <button
                onClick={() => {
                  if (draft.is_checklist) {
                    // Checklist -> rich text: wrap each item in a <p> so the WYSIWYG
                    // editor renders them on separate lines (not as raw HTML).
                    const html = (draft.checklist_items || [])
                      .map((i) => `<p>${(i.checked ? "✓ " : "") + escapeHtml(i.text || "")}</p>`)
                      .join("");
                    setDraft({ ...draft, is_checklist: false, content: html || "<p><br></p>", checklist_items: [] });
                  } else {
                    // Rich text -> checklist: strip HTML tags and split by block
                    // boundaries so the user never sees <ul><li>...</li></ul> as text.
                    const items = htmlToLines(draft.content || "")
                      .map((l) => ({ id: genId(), text: l, checked: false }));
                    setDraft({ ...draft, is_checklist: true, checklist_items: items.length ? items : [{ id: genId(), text: "", checked: false }], content: "" });
                  }
                }}
                className="h-8 sm:h-9 shrink-0 px-2 sm:px-3 rounded-full text-xs sm:text-sm hover:bg-stone-200/60 dark:hover:bg-stone-700/60 whitespace-nowrap"
                data-testid="editor-toggle-checklist"
              >
                {draft.is_checklist ? t("editor.toText") : t("editor.toChecklist")}
              </button>

              {/* Voice-note recorder (opens AudioRecorder dialog) */}
              <button
                onClick={() => setAudioOpen(true)}
                className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                data-testid="editor-mic"
                aria-label="Record voice note"
                title="Record voice note"
              >
                <Mic className="h-4 w-4" />
              </button>

              {/* Camera (take photo). Mobile-only — on desktop users have
                  the image dropzone at the top of the editor, so a tiny
                  camera icon in the toolbar was redundant clutter. */}
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={cameraUploading}
                className="md:hidden h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60 disabled:opacity-50"
                data-testid="editor-camera"
                aria-label="Take a picture"
                title="Take a picture"
              >
                <Camera className="h-4 w-4" />
              </button>

              {/* Find in note */}
              <button
                onClick={() => setFindOpen((v) => !v)}
                className={cn(
                  "h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60",
                  findOpen && "text-amber-700 dark:text-amber-300"
                )}
                data-testid="editor-find"
                aria-label="Find in note"
                title="Find in this note (⌘F)"
              >
                <Search className="h-4 w-4" />
              </button>
              <button
                onClick={() => onArchive(draft.id, !draft.archived)}
                className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                data-testid="editor-archive"
                aria-label="Archive"
              >
                {draft.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              </button>
              <button
                onClick={() => { onTrash(draft.id); onClose(); }}
                className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                data-testid="editor-trash"
                aria-label="Trash"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <button
                onClick={toggleLock}
                className={cn(
                  "h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60",
                  draft.locked && "text-amber-700 dark:text-amber-300"
                )}
                data-testid="editor-lock"
                aria-label={draft.locked ? "Unlock" : "Lock"}
                title={draft.locked ? "Unlock note" : "Lock note with passphrase"}
              >
                {draft.locked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setShareOpen(true)}
                className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                data-testid="editor-share"
                aria-label="Share"
                title="Share this note"
              >
                <Share2 className="h-4 w-4" />
              </button>

              {/* Dedicated Labels button so the user can tag the open note
                  without digging into the 3-dot menu. Shows the current
                  label count as a tiny badge when there's at least one. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      "relative h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60",
                      (draft.labels || []).length > 0 && "text-amber-700 dark:text-amber-300"
                    )}
                    data-testid="editor-labels-chip"
                    aria-label="Labels"
                    title="Add labels"
                  >
                    <Tag className="h-4 w-4" />
                    {(draft.labels || []).length > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-amber-500 text-white text-[9px] leading-[14px] text-center">
                        {draft.labels.length}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-72 overflow-auto">
                  <DropdownMenuLabel>Add to label</DropdownMenuLabel>
                  {labels.length === 0 && (
                    <div className="px-2 py-1 text-xs text-stone-500">No labels yet — create one from the sidebar</div>
                  )}
                  {labels.map((l) => (
                    <DropdownMenuCheckboxItem
                      key={l.id}
                      checked={(draft.labels || []).includes(l.name)}
                      onCheckedChange={() => toggleLabel(l.name)}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`editor-label-chip-${l.name}`}
                    >
                      {l.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {onManageLabels && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={(e) => { e.preventDefault(); createLabelAndAttach(); }}
                        data-testid="editor-labels-chip-create"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Create new label…
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-200/60 dark:hover:bg-stone-700/60"
                    data-testid="editor-more-menu"
                    aria-label="More"
                    title="More"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={duplicate} data-testid="editor-duplicate">
                    <Copy className="h-4 w-4 mr-2" /> Make a copy
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setVersionsOpen(true)} data-testid="editor-version-history">
                    <History className="h-4 w-4 mr-2" /> Version history
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Labels</DropdownMenuLabel>
                  {onManageLabels && (
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); createLabelAndAttach(); }}
                      data-testid="editor-create-label"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Create new label…
                    </DropdownMenuItem>
                  )}
                  {labels.length === 0 && (
                    <div className="px-2 py-1 text-xs text-stone-500">No labels yet — tap “Create new label…” above</div>
                  )}
                  {labels.map((l) => (
                    <DropdownMenuCheckboxItem
                      key={l.id}
                      checked={(draft.labels || []).includes(l.name)}
                      onCheckedChange={() => toggleLabel(l.name)}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`editor-label-toggle-${l.name}`}
                    >
                      {l.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button onClick={commit} data-testid="editor-save" className="rounded-full shrink-0 h-8 sm:h-9 px-3 sm:px-4 text-xs sm:text-sm">
              {t("editor.close")}
            </Button>
          </div>
        </div>
        )}
      </DialogContent>
      <VersionsDialog
        noteId={draft?.id}
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        onRestored={(fresh) => { setDraft(fresh); }}
      />
      <ShareDialog
        note={draft}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
      <AudioRecorder
        open={audioOpen}
        onOpenChange={setAudioOpen}
        onSave={onAudioSaved}
      />
      {/* Hidden input for the Camera button. `capture="environment"` tells
          phone browsers to prefer the rear camera; desktop Chromebooks also
          respect this hint. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={onCameraChange}
        data-testid="editor-camera-input"
      />
    </Dialog>
  );
}
